// src/components/stats/OperationsPanel.js
//
// "Şu an ne bozuk" bloğu. Tarih aralığından bağımsızdır (uç parametre almaz),
// bu yüzden veriyi kendisi çeker ve 60 saniyede bir tazeler.
//
// Ayrımlar bilinçli:
//   • 24 saati geçen bekleyen sipariş = terk edilmiş sepet ya da takılmış akış → uyarı
//   • son 24 saatte açılan bekleyen sipariş = normal olabilir → nötr
//   • "başarılı ama eSIM'i yok" = kullanıcı ödedi, ürünü almadı → kırmızı

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './statsBlocks.module.css';
import { fetchOperations, readStatsError, formatCount, formatDateTime } from '../../services/statsApi';

const REFRESH_MS = 60000;

// Ham durum kodları operatöre bir şey anlatmıyor; bilinenler Türkçeye çevrilir,
// kod küçük punto olarak altında kalır (destek yazışmasında lazım oluyor).
const STATUS_LABELS = {
    FAILED_PROVIDER_API_ERROR: 'Sağlayıcı API hatası',
    FAILED_PROVIDER_TIMEOUT: 'Sağlayıcı yanıt vermedi',
    FAILED_PAYMENT: 'Ödeme alınamadı',
    FAILED_STORE_VERIFICATION: 'Mağaza doğrulaması başarısız',
    PENDING_PROVIDER_ASYNC: 'Sağlayıcı bekleniyor',
    PROVISIONED_SUCCESSFULLY: 'Teslim edildi',
};

const statusLabel = (status) => STATUS_LABELS[status] || status;

const OperationsPanel = () => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const result = await fetchOperations();
            setData(result);
            setError(null);
        } catch (err) {
            console.error('Operasyon verisi alınamadı:', err);
            setError(readStatsError(err, 'Operasyon verisi alınamadı.').message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        timerRef.current = setInterval(() => load({ silent: true }), REFRESH_MS);
        return () => clearInterval(timerRef.current);
    }, [load]);

    if (isLoading && !data) {
        return (
            <section className={styles.block}>
                <header className={styles.blockHeader}><h2 className={styles.blockTitle}>Operasyon</h2></header>
                <p className={styles.muted}>Yükleniyor…</p>
            </section>
        );
    }

    if (error && !data) {
        return (
            <section className={styles.block}>
                <header className={styles.blockHeader}><h2 className={styles.blockTitle}>Operasyon</h2></header>
                <p className={styles.errorText}>{error}</p>
                <button type="button" className="ghost" onClick={() => load()}>Tekrar dene</button>
            </section>
        );
    }

    const stuck = data?.stuckOrders || {};
    const byPlatform = Object.entries(stuck.olderThan24hByPlatform || {});
    const failures = data?.failures || [];
    const recent = data?.recentFailedOrders || [];
    const esimless = Number(data?.esimlessSuccessfulOrders) || 0;

    return (
        <section className={styles.block}>
            <header className={styles.blockHeader}>
                <h2 className={styles.blockTitle}>Operasyon</h2>
                <span className={styles.blockMeta}>
                    {formatDateTime(data?.generatedAt)} · dakikada bir tazelenir
                </span>
            </header>

            {/* Sessiz teslimat kaybı: ödendi ama eSIM yok. Sıfırdan farklıysa acil. */}
            {esimless > 0 && (
                <div className={`${styles.alert} ${styles.alertDanger}`}>
                    <strong className="num">{formatCount(esimless)}</strong> başarılı siparişte eSIM kaydı yok —
                    kullanıcı ödedi ama ürünü almadı. Acil inceleyin.
                </div>
            )}

            <div className={styles.factRow}>
                <div className={`${styles.fact} ${stuck.olderThan24hTotal > 0 ? styles.factWarn : ''}`}>
                    <span className={styles.factLabel}>24 saati geçen bekleyen</span>
                    <span className={`${styles.factValue} num`}>{formatCount(stuck.olderThan24hTotal || 0)}</span>
                    {byPlatform.length > 0 && (
                        <span className={styles.factHint}>
                            {byPlatform.map(([platform, count]) => `${platform} ${count}`).join(' · ')}
                        </span>
                    )}
                    {stuck.oldestCreatedAt && (
                        <span className={styles.factHint}>en eskisi {formatDateTime(stuck.oldestCreatedAt)}</span>
                    )}
                </div>

                <div className={styles.fact}>
                    <span className={styles.factLabel}>Son 24 saatte açılan</span>
                    <span className={`${styles.factValue} num`}>{formatCount(stuck.within24h || 0)}</span>
                    <span className={styles.factHint}>henüz ödenmemiş olabilir, normal</span>
                </div>

                <div className={styles.fact}>
                    <span className={styles.factLabel}>Manuel sipariş</span>
                    <span className={`${styles.factValue} num`}>{formatCount(data?.manualOrders || 0)}</span>
                </div>

                <div className={`${styles.fact} ${esimless > 0 ? styles.factDanger : ''}`}>
                    <span className={styles.factLabel}>eSIM'siz başarılı sipariş</span>
                    <span className={`${styles.factValue} num`}>{formatCount(esimless)}</span>
                </div>
            </div>

            {failures.length > 0 && (
                <>
                    <h3 className={styles.subTitle}>Hata dağılımı</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Durum</th>
                                <th>Platform</th>
                                <th className={styles.numCol}>Adet</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failures.map((f, i) => (
                                <tr key={`${f.status}-${f.platform}-${i}`}>
                                    <td>
                                        <div className={styles.statusName}>{statusLabel(f.status)}</div>
                                        <div className={styles.statusCode}>{f.status}</div>
                                    </td>
                                    <td>{f.platform}</td>
                                    <td className={`${styles.numCol} num`}>{formatCount(f.count)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {recent.length > 0 && (
                <>
                    <h3 className={styles.subTitle}>Son hatalı siparişler</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Sipariş</th>
                                <th>Platform</th>
                                <th>Durum</th>
                                <th>Tarih</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map((order) => (
                                <tr key={order.orderId}>
                                    <td>
                                        <span className={`${styles.orderId} num`} title={order.orderId}>
                                            {String(order.orderId).slice(0, 8)}
                                        </span>
                                        {order.errorMessage && (
                                            <span className={styles.errorHint} title={order.errorMessage}>
                                                {order.errorMessage}
                                            </span>
                                        )}
                                    </td>
                                    <td>{order.platform}</td>
                                    <td>
                                        <div className={styles.statusName}>{statusLabel(order.status)}</div>
                                        <div className={styles.statusCode}>{order.status}</div>
                                    </td>
                                    <td className={styles.nowrap}>{formatDateTime(order.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {failures.length === 0 && recent.length === 0 && esimless === 0 && (
                <p className={styles.allClear}>✓ Bekleyen bir hata kaydı yok.</p>
            )}
        </section>
    );
};

export default OperationsPanel;
